import React from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { ValidationIssue } from 'api'
import { maxSeverity, Severity } from 'model'
import * as constants from 'view/styles/constants'
import { ValidationTooltip } from '../tooltip'

export interface Focusable {
  setFocus(): void
}

export interface InputProps {
  id?: string
  className?: string
  disabled?: boolean
  readOnly?: boolean
  validationIssues?: ValidationIssue[]
  onChange?: () => void
  onUpdate?: (value: any, ...rest: any[]) => void
  dataTestId?: string
}

const inputBoxBase = css`
  height: 26px;
  width: 100%;
  padding: 0 6px;
  font-size: 12px;
  border-width: 1px;
  border-style: solid;
  border-radius: 6px;
  background-color: ${constants.CORE_PRIMARY};
  outline: none;
`

export enum BorderModifier {
  Normal,
  Hover,
  Focused
}

export interface BaseInputProps {
  disabled: boolean | undefined
  readOnly: boolean | undefined
  validationIssues: ValidationIssue[] | undefined
}

export const StyledInput = styled.input<BaseInputProps>`
  ${inputBoxBase};
  color: ${props => getColour(props)};
  border-color: ${props => getBorderColour(props, BorderModifier.Normal)};
  &:focus-within {
    border-color: ${props => getBorderColour(props, BorderModifier.Focused)};
  }
  &:hover:not(:focus-within) {
    border-color: ${props => getBorderColour(props, BorderModifier.Hover)};
  }
  background-image: ${props => getBackgroundImage(props)};
  background-size: 10px 10px;
  background-position: top right;
  border-top-right-radius: ${props => getBorderRadiusTopRight(props)};
  &::selection,
  & *::selection {
    background-color: ${props => getSelectionColour(props)};
  }
`

type StyledInputProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & BaseInputProps

export const BaseInput = React.forwardRef<HTMLInputElement, StyledInputProps>((props, ref) => (
  <ValidationTooltip validationIssues={props.validationIssues || []}>
    <StyledInput ref={ref} {...props} />
  </ValidationTooltip>
))

const StyledSpan = StyledInput.withComponent('span')

export const BaseInputSpan = React.forwardRef<HTMLSpanElement, StyledInputProps>((props, ref) => (
  <ValidationTooltip validationIssues={props.validationIssues || []}>
    <StyledSpan ref={ref} {...props} />
  </ValidationTooltip>
))

function getSelectionColour(props: BaseInputProps) {
  const severity = maxSeverity(props.validationIssues)

  if (severity === Severity.ERROR) {
    return constants.ACCENT_BAD_3
  } else if (severity === Severity.WARN) {
    return constants.ACCENT_BE_AWARE_3
  } else if (severity === Severity.INFO) {
    return constants.ACCENT_GOOD_3
  } else {
    return constants.ACCENT_PRIMARY_3
  }
}

export function getColour(props: BaseInputProps) {
  if (props.readOnly || props.disabled) {
    return constants.CORE_PRIMARY_4
  } else {
    return constants.CORE_PRIMARY_7
  }
}

function getBackgroundImage(props: BaseInputProps) {
  const severity = maxSeverity(props.validationIssues)

  if (severity === Severity.ERROR) {
    return `linear-gradient(to top right, transparent 50%, ${constants.ACCENT_BAD} 50%)`
  } else if (severity === Severity.WARN) {
    return `linear-gradient(to top right, transparent 50%, ${constants.ACCENT_BE_AWARE} 50%)`
  } else if (severity === Severity.INFO) {
    return `linear-gradient(to top right, transparent 50%, ${constants.ACCENT_GOOD} 50%)`
  } else {
    return undefined
  }
}

function getBorderRadiusTopRight(props: BaseInputProps) {
  const severity = maxSeverity(props.validationIssues)
  return severity ? 0 : undefined
}

export function getBorderColour(props: BaseInputProps, modifier: BorderModifier) {
  const severity = maxSeverity(props.validationIssues)

  if (severity === Severity.ERROR) {
    return constants.ACCENT_BAD
  } else if (severity === Severity.WARN) {
    return constants.ACCENT_BE_AWARE
  } else if (severity === Severity.INFO) {
    return constants.ACCENT_GOOD
  } else if (props.disabled) {
    return constants.CORE_PRIMARY_2
  } else if (props.readOnly) {
    return constants.CORE_PRIMARY_2
  } else if (modifier === BorderModifier.Focused) {
    return constants.ACCENT_PRIMARY
  } else if (modifier === BorderModifier.Hover) {
    return constants.CORE_PRIMARY_5
  } else {
    return constants.CORE_PRIMARY_3
  }
}
